
import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { DatabaseStatus } from "./DatabaseStatus";
import { ActionButtons } from "./ActionButtons";
import { StructureChecker } from "./StructureChecker";
import { ColumnInfo } from "@/types/dbSchema";

interface SubscriptionActionsProps {
  onProcessPayments: () => void;
  onExport: () => void;
  isProcessingPayments: boolean;
  isExporting: boolean;
}

export function SubscriptionActions({
  onProcessPayments,
  onExport,
  isProcessingPayments,
  isExporting
}: SubscriptionActionsProps) {
  const { user, isAdmin } = useAuth();
  const [dbStatus, setDbStatus] = useState<string>("Checking...");
  const [subscriptionCount, setSubscriptionCount] = useState<number>(0);
  
  // Expected table structure - updated to match all columns in the table
  const expectedColumns = [
    { column_name: "id", data_type: "uuid", is_nullable: "NO" },
    { column_name: "customer_name", data_type: "text", is_nullable: "NO" },
    { column_name: "email", data_type: "text", is_nullable: "YES" },
    { column_name: "address", data_type: "text", is_nullable: "YES" },
    { column_name: "postcode", data_type: "text", is_nullable: "YES" },
    { column_name: "bin_day", data_type: "text", is_nullable: "YES" },
    { column_name: "start_date", data_type: "timestamp with time zone", is_nullable: "YES" },
    { column_name: "weekly_rate", data_type: "numeric", is_nullable: "YES" },
    { column_name: "total_paid", data_type: "numeric", is_nullable: "YES" },
    { column_name: "created_at", data_type: "timestamp with time zone", is_nullable: "YES" },
    { column_name: "updated_at", data_type: "timestamp with time zone", is_nullable: "YES" },
    { column_name: "active", data_type: "boolean", is_nullable: "YES" },
    { column_name: "status", data_type: "text", is_nullable: "YES" },
    { column_name: "end_date", data_type: "timestamp with time zone", is_nullable: "YES" }
  ];
  
  // Check database status when component mounts
  useEffect(() => {
    const checkDatabase = async () => {
      try {
        const { data, error } = await supabase
          .from('subscriptions')
          .select('count');
          
        if (error) {
          console.error("Database check error:", error);
          setDbStatus(`Error: ${error.message}`);
        } else {
          const count = data?.[0]?.count || 0;
          setSubscriptionCount(Number(count));
          setDbStatus(count > 0 ? `Connected (${count} subscriptions found)` : "Connected (no subscriptions)");
        }
      } catch (err) {
        console.error("Database connection error:", err);
        setDbStatus(`Connection error: ${err instanceof Error ? err.message : String(err)}`);
      }
    };
    
    // Only run check for admin users
    if (isAdmin) {
      checkDatabase();
    }
    
    console.log("SubscriptionActions rendering with user:", user?.email);
    console.log("Is admin check result:", isAdmin);
  }, [user, isAdmin]);
  
  // Don't render anything for non-admin users
  if (!isAdmin) {
    console.log("SubscriptionActions: Not admin, hiding component");
    return null;
  }
  
  console.log("SubscriptionActions: Admin user detected, showing admin controls");
  
  return (
    <div className="bg-white p-4 rounded-lg shadow-md mb-8 border-2 border-red-500 flex flex-col items-center space-y-4" data-testid="subscription-actions">
      <h2 className="text-xl font-bold mb-2 text-center text-red-500">ADMIN CONTROLS</h2>
      <p className="mb-2 text-center">Logged in as: {user?.email}</p>
      
      {/* Database status indicator */}
      <DatabaseStatus 
        dbStatus={dbStatus} 
        subscriptionCount={subscriptionCount} 
      />
      
      <ActionButtons 
        onProcessPayments={onProcessPayments}
        onExport={onExport}
        isProcessingPayments={isProcessingPayments}
        isExporting={isExporting}
      />
      
      {/* Table structure checker */}
      <StructureChecker expectedColumns={expectedColumns} />
    </div>
  );
}
